/**
 * Splitting one tested item into several listings (Trac #1111). Pure rules
 * only, so the Testing screen and its tests share one implementation.
 *
 * The worker re-checks every rule here (warehouse-system utils/split-parts.js),
 * and the cost shares must add up to the line's buy price to the penny.
 * Money is whole pence inside this module. The screen holds pounds as text,
 * because that is what the operator types and what the worker accepts.
 */

import { itemQuantity } from '../../../../shared/item-count.js'
import { toPounds } from '../pricing/repriceMath.js'

/** Same bounds as the worker's planSplit. */
export const MIN_SPLIT_PARTS = 2
export const MAX_SPLIT_PARTS = 20

export const PART_CONDITIONS = ['Fair', 'Good', 'Very Good', 'Excellent', 'Mint']
export const PHYSICAL_FIELD_KEYS = ['scratching', 'dings', 'colouration', 'cleanliness', 'integrity']
// Least thorough first, so the line's record never claims more than a part got.
const TESTING_LEVEL_RANK = ['Untested', 'Power On Only', 'Partially Tested', 'Fully Tested']
const SCORE_RANK = ['Fair', 'Good', 'Very Good', 'Excellent', 'Mint']

/**
 * Pounds typed by an operator to whole pence, or null when it is blank,
 * negative, not a number, or has more than two decimal places.
 */
export function parsePounds(text) {
  if (text == null || String(text).trim() === '') return null
  const pounds = Number(text)
  if (!Number.isFinite(pounds) || pounds < 0) return null
  const pence = Math.round(pounds * 100)
  return Math.abs(pounds * 100 - pence) < 1e-6 ? pence : null
}

/** The whole line's buy price in pence: unit price times quantity. */
export function lineBuyPence(item) {
  return Math.round((Number(item?.buyPrice) || 0) * 100) * itemQuantity(item)
}

/**
 * Each part's default cost share, in pence, in part order.
 *
 * A part's share follows its share of the total sell price. Every part but the
 * largest rounds to the nearest penny and the largest takes the remainder, so
 * the shares always add up to `lineBuyPence` exactly. If sell prices are all
 * zero the buy price is shared evenly.
 */
export function defaultCostShares(sellPence, lineBuy) {
  if (sellPence.length === 0) return []
  const totalSell = sellPence.reduce((sum, sell) => sum + sell, 0)
  const weights = totalSell === 0 ? sellPence.map(() => 1) : sellPence
  const totalWeight = totalSell === 0 ? sellPence.length : totalSell
  const largest = weights.indexOf(Math.max(...weights))

  const withRounding = (round) => {
    const shares = weights.map((weight) => round((lineBuy * weight) / totalWeight))
    const others = shares.reduce((sum, share, i) => (i === largest ? sum : sum + share), 0)
    shares[largest] = lineBuy - others
    return shares
  }
  const nearest = withRounding(Math.round)
  return nearest[largest] >= 0 ? nearest : withRounding(Math.floor)
}

/** One empty part. */
export function newPart() {
  return {
    name: '',
    mainCategory: '',
    subCategory: '',
    condition: '',
    sellPrice: '',
    costShare: '',
    physicalCondition: Object.fromEntries(PHYSICAL_FIELD_KEYS.map((key) => [key, null])),
    testingLevel: null,
    checklistResults: {},
    includedItems: [],
    otherAccessories: '',
    notes: ''
  }
}

/** A new split: two empty parts, with default shares. */
export function startSplit(lineBuy) {
  return refreshShares({ parts: [newPart(), newPart()], sharesEdited: false }, lineBuy)
}

export function addPart(split, lineBuy) {
  if (split.parts.length >= MAX_SPLIT_PARTS) return split
  return refreshShares({ ...split, parts: [...split.parts, newPart()] }, lineBuy)
}

export function removePart(split, index, lineBuy) {
  if (split.parts.length <= MIN_SPLIT_PARTS) return split
  return refreshShares({ ...split, parts: split.parts.filter((_, i) => i !== index) }, lineBuy)
}

/**
 * Change one part. A sell price change re-shares the cost, unless the operator
 * has edited a share by hand: after that the shares stay as typed and the
 * footer says what is left to fix.
 */
export function updatePart(split, index, patch, lineBuy) {
  const parts = split.parts.map((part, i) => (i === index ? { ...part, ...patch } : part))
  const next = { ...split, parts, sharesEdited: split.sharesEdited || 'costShare' in patch }
  return 'sellPrice' in patch ? refreshShares(next, lineBuy) : next
}

/** Put the default shares on every part, unless the operator has edited them. */
export function refreshShares(split, lineBuy) {
  if (split.sharesEdited) return split
  const sells = split.parts.map((part) => parsePounds(part.sellPrice) ?? 0)
  const shares = defaultCostShares(sells, lineBuy)
  return { ...split, parts: split.parts.map((part, i) => ({ ...part, costShare: toPounds(shares[i]) })) }
}

/**
 * The footer figures: total sell, total cost, and how far the shares are from
 * the buy price. `balanced` is true only when every share is a valid amount and
 * they add up to the buy price to the penny.
 */
export function splitFooter(split, lineBuy) {
  const sellPence = split.parts.reduce((sum, part) => sum + (parsePounds(part.sellPrice) ?? 0), 0)
  const shares = split.parts.map((part) => parsePounds(part.costShare))
  const costPence = shares.reduce((sum, share) => sum + (share ?? 0), 0)
  const differencePence = costPence - lineBuy
  return {
    sellPence,
    costPence,
    differencePence,
    balanced: shares.every((share) => share !== null) && differencePence === 0
  }
}

/** What is still missing from one part, as short phrases. */
export function partProblems(part, label) {
  const problems = []
  if (!part.name.trim()) problems.push(`${label} needs a name`)
  if (!part.mainCategory || !part.subCategory) problems.push(`${label} needs a category`)
  if (!part.condition) problems.push(`${label} needs a condition`)
  if (parsePounds(part.sellPrice) === null) problems.push(`${label} needs a sell price`)
  if (parsePounds(part.costShare) === null) problems.push(`${label} needs a cost share`)
  return problems
}

/** What is still missing from a part's own test. */
export function partTestProblems(part, label) {
  const problems = []
  if (!PHYSICAL_FIELD_KEYS.every((key) => part.physicalCondition[key])) problems.push(`${label}: complete the physical assessment`)
  if (!part.testingLevel) problems.push(`${label}: select a testing level`)
  return problems
}

/** Everything that stops a split being saved. Empty means it can be saved. */
export function splitProblems(split, lineBuy) {
  const problems = []
  if (split.parts.length < MIN_SPLIT_PARTS) problems.push(`A split needs at least ${MIN_SPLIT_PARTS} parts`)
  split.parts.forEach((part, i) => {
    const label = `Part ${i + 1}`
    problems.push(...partProblems(part, label), ...partTestProblems(part, label))
  })
  const footer = splitFooter(split, lineBuy)
  if (footer.differencePence !== 0) {
    problems.push(`Cost shares are ${toPounds(Math.abs(footer.differencePence))} ${footer.differencePence > 0 ? 'over' : 'under'} the buy price`)
  }
  return problems
}

/**
 * The line's own record, built from its parts. Prism keeps one testing record
 * per line, so it takes the worst part condition, the least thorough testing
 * level and the worst score for each physical field. The checklists differ per
 * part, so they stay on the parts.
 */
export function wholeItemRecord(parts) {
  const worst = (values, rank) => values.filter(Boolean).sort((a, b) => rank.indexOf(a) - rank.indexOf(b))[0] ?? null
  const physicalCondition = Object.fromEntries(
    PHYSICAL_FIELD_KEYS.map((key) => [key, worst(parts.map((part) => part.physicalCondition[key]), SCORE_RANK)])
  )
  const notes = parts
    .filter((part) => part.notes.trim())
    .map((part) => `${part.name.trim() || 'Part'}: ${part.notes.trim()}`)
    .join('\n')
  return {
    overallCondition: worst(parts.map((part) => part.condition), PART_CONDITIONS),
    testingLevel: worst(parts.map((part) => part.testingLevel), TESTING_LEVEL_RANK),
    physicalCondition,
    checklistResults: {},
    includedItems: [],
    otherAccessories: '',
    notes
  }
}

/** The label for the finish button. */
export function splitDoneLabel(split) {
  return `Done · Create ${split.parts.length} listings`
}
